import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Alert, Box, Button, Chip, Typography } from '@mui/material';

import {

  Assignment as AssignmentIcon,

  OndemandVideo as VideoIcon,

} from '@mui/icons-material';

import { useNavigate, useSearchParams } from 'react-router-dom';

import {

  ContestDetailAccordion,

  ContestFrameShell,

  ContestScorecardPanel,

  ContestVideoPanel,

} from '@components/contest/PublicContestFrames';

import ContestVideoCompareDialog from '@components/contest/ContestVideoCompareDialog';

import {

  ContestBenchmarkComparePanel,

  ContestLearnAgeActions,

  ContestSurveyPanel,

  seedCompareAges,

} from '@components/contest/ContestWorkspaceChrome';

import ContestSelfEvalTabPanel from '@components/contest/ContestSelfEvalTabPanel';

import ContestSubmissionTabPanel from '@components/contest/ContestSubmissionTabPanel';

import ContestLeaderboardTabPanel from '@components/contest/ContestLeaderboardTabPanel';

import { ContestBenchmarkTabPanel } from '@components/contest/ContestSkillBenchmarkBlock';
import { ContestSportsIqTabPanel } from '@components/contest/ContestSportsIqTabPanel';

import {

  ContestJourneyHeader,

  contestTabFromQuery,

  contestTabToQuery,

  type ContestJourneyTab,

} from '@components/contest/ContestJourneyChrome';

import { brandColors } from '@config/brandTokens';

import { getContestSurvey } from '@config/contestSurveys';

import type { ContestCategoryId, ContestGenderKey, ContestSectionKey, ContestTypeId, SportContestCatalog } from '../../types/contestCatalog';

import { getSportIcon } from '@utils/sportPresentation';

import { toSportSlug } from '@utils/sports';

import {

  CONTEST_AGE_CATEGORIES,

  CONTEST_TYPE_OPTIONS,

  findContestInCatalog,

  getContestsGroupedByCategory,

  getSportContestCatalog,

  getVisibleFrame2Sections,

  hasContestCatalogPreview,

  getContestGenderKeys,

  getContestGenderLabel,

} from '@utils/contestCatalog';

import { loadMergedSportContestCatalog } from '@utils/contestCatalogApi';

import { clearLegacyContestSubmissionPreview } from '@utils/contestEntrySubmission';

import { fetchPlayerSubmittedCatalogIds } from '@utils/contestSubmissionStatus';

import { useActiveSession } from '@hooks/useActiveSession';

import { useAuthStore } from '@store/authStore';
import { actingPlayerContestGender, useContestActingPlayer } from '@hooks/useContestActingPlayer';
import ContestActingPlayerSwitch from '@components/contest/ContestActingPlayerSwitch';



const ROUTE_TO_CATALOG_CATEGORY: Record<string, ContestCategoryId> = {

  skills: 'skill',

  skill: 'skill',

  drills: 'drill',

  drill: 'drill',

  fitness: 'fitness',

  tactical: 'tactical',

  match_intelligence: 'match_intelligence',

};



export interface ContestCatalogExperienceProps {

  sportName: string;

  categoryFilter?: string;

  initialAgeCategory?: string;

  initialContestId?: string | null;

  initialTab?: ContestJourneyTab;

  uploadMode?: boolean;

  playerId?: string;

  playerName?: string;

  userId?: string;

  frameHeight?: string | Record<string, string | number>;

  /** Bump to refetch DB catalog (e.g. admin preview after edits). */
  catalogRefreshKey?: number;

  /** Admin preview: show whether Frame 1 is from DB or JSON seed. */
  showCatalogSourceHint?: boolean;

  onSubmit?: (contestId: string, ageCategory: string) => void;

  onSelfEvaluate?: (ageCategory: string) => void;

  onUploadDismiss?: () => void;

}



const COLUMN_GRID = {

  xs: '1fr',

  md: '1fr',

  lg: 'minmax(0, 1.16fr) minmax(0, 1.64fr) minmax(0, 0.7fr)',

};



const ContestColumn: React.FC<{ children: React.ReactNode }> = ({ children }) => (

  <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%', flex: 1 }}>{children}</Box>

);



const ContestCatalogExperience: React.FC<ContestCatalogExperienceProps> = ({

  sportName,

  categoryFilter = 'all',

  initialAgeCategory,

  initialContestId,

  initialTab,

  uploadMode = false,

  playerId,

  playerName,

  userId,

  frameHeight,

  catalogRefreshKey = 0,

  showCatalogSourceHint = false,

}) => {

  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();

  const { signedIn: sessionSignedIn, user: sessionUser } = useActiveSession();
  const authUser = useAuthStore((s) => s.user);
  const authIsAuthenticated = useAuthStore((s) => s.isAuthenticated);
  // Portal pages pass playerId/userId; prefer those + auth store so contest mount never "logs out"
  const signedIn =
    Boolean(userId || playerId) || authIsAuthenticated || sessionSignedIn;
  const user = authUser || sessionUser;
  const {
    selected: actingPlayer,
    options: actingOptions,
    viewOnly: contestViewOnly,
    canSwitch: canSwitchActingPlayer,
    setActingPlayerId,
  } = useContestActingPlayer(user);
  const contestPlayerId = actingPlayer?.playerId || playerId || user?.playerId;
  const contestPlayerName =
    actingPlayer?.displayName ||
    playerName ||
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    user?.email;
  const profileAgeCategory = actingPlayer?.ageCategory || null;
  const missingDob = Boolean(signedIn && (actingPlayer ? actingPlayer.missingDob : !contestPlayerId));

  const sportSlug = toSportSlug(sportName);

  const [sportCatalog, setSportCatalog] = useState<SportContestCatalog | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogSource, setCatalogSource] = useState<'loading' | 'db' | 'seed'>('loading');

  useEffect(() => {
    let cancelled = false;
    setCatalogLoading(true);
    setCatalogSource('loading');
    void loadMergedSportContestCatalog(sportName, 'foundation').then(({ catalog, fromDb }) => {
      if (cancelled) return;
      setSportCatalog(catalog);
      setCatalogSource(fromDb ? 'db' : 'seed');
      setCatalogLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [sportName, catalogRefreshKey]);

  const hasPreview = Boolean(sportCatalog) || hasContestCatalogPreview(sportName);



  const [contestTypeId] = useState<ContestTypeId>('foundation');

  const initialAge =
    actingPlayer?.ageCategory ||
    (actingPlayer?.missingDob ? '' : initialAgeCategory || searchParams.get('age') || 'U13');

  const [ageCategory, setAgeCategory] = useState<string>(initialAge);

  const [activeTab, setActiveTab] = useState<ContestJourneyTab>(

    initialTab ?? contestTabFromQuery(searchParams.get('tab'))

  );

  const [compareMode, setCompareMode] = useState(false);

  const [compareAges, setCompareAges] = useState<string[]>([initialAge]);

  const [surveyOpen, setSurveyOpen] = useState(false);

  const [videoCompareOpen, setVideoCompareOpen] = useState(false);

  const [genderKey, setGenderKey] = useState<ContestGenderKey>(() => {
    const fromProfile = actingPlayerContestGender(actingPlayer);
    if (fromProfile) return fromProfile;
    try {
      const stored = sessionStorage.getItem('encycgames.contestGender');
      if (stored === 'boys' || stored === 'girls' || stored === 'open' || stored === 'diverse') {
        return stored;
      }
    } catch {
      /* ignore */
    }
    return 'boys';
  });

  const [selectedContestId, setSelectedContestId] = useState<string | null>(null);

  const [activeSectionKey, setActiveSectionKey] = useState<ContestSectionKey>('liability_disclaimer');

  const [activeVideoId, setActiveVideoId] = useState<string | null>(null);

  const [submittedIds, setSubmittedIds] = useState<string[]>([]);

  const detailsColumnRef = useRef<HTMLDivElement>(null);

  const handleSelectContest = useCallback((contestId: string) => {
    setSelectedContestId(contestId);
    // When columns are stacked, bring Skill and Performance Guidelines into view so the user
    // does not have to scroll back up from a bottom row in Frame 1.
    requestAnimationFrame(() => {
      const stacked = typeof window !== 'undefined' && window.matchMedia('(max-width: 1199.95px)').matches;
      if (stacked) {
        detailsColumnRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }, []);



  useEffect(() => {

    clearLegacyContestSubmissionPreview();

  }, []);



  useEffect(() => {

    if (initialAgeCategory) {

      setAgeCategory(initialAgeCategory);

      setCompareAges([initialAgeCategory]);

    }

  }, [initialAgeCategory]);



  useEffect(() => {

    if (initialTab) setActiveTab(initialTab);

  }, [initialTab]);



  useEffect(() => {

    const next = new URLSearchParams(searchParams);

    const tabQuery = contestTabToQuery(activeTab);

    if (tabQuery) next.set('tab', tabQuery);

    else next.delete('tab');

    if (!ageCategory || ageCategory === 'U13') next.delete('age');

    else next.set('age', ageCategory);

    setSearchParams(next, { replace: true });

  }, [activeTab, ageCategory]); // eslint-disable-line react-hooks/exhaustive-deps



  useEffect(() => {

    if (uploadMode && searchParams.get('contest')) {

      setActiveTab('contest');

    }

  }, [uploadMode, searchParams]);



  const categoryGroups = useMemo(() => {

    if (!sportCatalog) return [];

    const groups = getContestsGroupedByCategory(sportCatalog, contestTypeId);

    const mapped = ROUTE_TO_CATALOG_CATEGORY[String(categoryFilter || 'all').toLowerCase()];

    if (!mapped) return groups;

    return groups.filter((g) => g.id === mapped);

  }, [sportCatalog, contestTypeId, categoryFilter]);



  const allContests = useMemo(

    () => categoryGroups.flatMap((g) => g.contests),

    [categoryGroups]

  );



  const refreshSubmissionStatus = useCallback(async () => {

    if (!contestPlayerId) {

      setSubmittedIds([]);

      return;

    }

    const ids = await fetchPlayerSubmittedCatalogIds(sportName, contestPlayerId, allContests);

    setSubmittedIds(ids);

  }, [contestPlayerId, sportName, allContests]);



  useEffect(() => {

    void refreshSubmissionStatus();

  }, [refreshSubmissionStatus]);



  const selectedContest = useMemo(() => {

    if (!sportCatalog || !selectedContestId) return null;

    return findContestInCatalog(sportCatalog, contestTypeId, selectedContestId)?.contest ?? null;

  }, [sportCatalog, contestTypeId, selectedContestId]);



  const skillCount = allContests.length;

  const foundationLabel = useMemo(() => {

    const opt = CONTEST_TYPE_OPTIONS.find((o) => o.id === contestTypeId);

    const base = opt?.label || 'Developmental & Performance Capabilities';

    return `${base}${skillCount ? ` (${skillCount} skill${skillCount === 1 ? '' : 's'})` : ''}`;

  }, [contestTypeId, skillCount]);



  useEffect(() => {

    if (allContests.length > 0) {

      setSelectedContestId((prev) => {

        if (initialContestId && allContests.some((c) => c.id === initialContestId)) {

          return initialContestId;

        }

        if (prev && allContests.some((c) => c.id === prev)) return prev;

        return allContests[0].id;

      });

    } else {

      setSelectedContestId(null);

    }

  }, [allContests, initialContestId]);



  useEffect(() => {

    const visible = getVisibleFrame2Sections(selectedContest);

    const firstVisible = visible[0]?.key || 'liability_disclaimer';

    setActiveSectionKey((prev) => (visible.some((section) => section.key === prev) ? prev : firstVisible));

    setActiveVideoId(null);

  }, [selectedContest?.id, selectedContest?.sections]);



  const handleAgeChange = (age: string) => {

    setAgeCategory(age);

    if (!compareMode) {

      setCompareAges([age]);

      return;

    }

    if (compareAges.includes(age)) {

      if (compareAges.length > 1) {

        setCompareAges(compareAges.filter((a) => a !== age));

      }

      return;

    }

    if (compareAges.length >= 3) {

      setCompareAges([...compareAges.slice(1), age]);

      return;

    }

    setCompareAges([...compareAges, age]);

  };



  useEffect(() => {
    if (!actingPlayer?.playerId) return;
    const nextAge = actingPlayer.ageCategory || '';
    setAgeCategory(nextAge);
    setCompareAges(nextAge ? [nextAge] : []);
    const nextGender = actingPlayerContestGender(actingPlayer);
    if (!nextGender) return;
    const allowed = getContestGenderKeys(actingPlayer.ageCategory || nextAge);
    if (!allowed.includes(nextGender)) return;
    setGenderKey(nextGender);
    try {
      sessionStorage.setItem('encycgames.contestGender', nextGender);
    } catch {
      /* ignore */
    }
  }, [actingPlayer?.playerId, actingPlayer?.ageCategory, actingPlayer?.gender, actingPlayer?.missingDob]);

  useEffect(() => {
    if (activeTab !== 'contest' && activeTab !== 'self_evaluation') return;
    if (actingPlayer) {
      const nextAge = actingPlayer.ageCategory || '';
      if (ageCategory !== nextAge) {
        setAgeCategory(nextAge);
        setCompareAges(nextAge ? [nextAge] : []);
      }
      return;
    }
    if (missingDob && ageCategory) {
      setAgeCategory('');
      setCompareAges([]);
    }
  }, [activeTab, actingPlayer, actingPlayer?.ageCategory, actingPlayer?.missingDob, missingDob, ageCategory]);

  const handleCompareModeChange = useCallback(

    (enabled: boolean) => {

      setCompareMode(enabled);

      if (enabled) {

        setCompareAges(seedCompareAges(ageCategory, CONTEST_AGE_CATEGORIES));

        setSurveyOpen(false);

      } else {

        setCompareAges([ageCategory]);

      }

    },

    [ageCategory],

  );



  const handleTabChange = (tab: ContestJourneyTab) => {

    setActiveTab(tab);

    if (tab === 'contest' || tab === 'self_evaluation') {
      if (actingPlayer) {
        const nextAge = actingPlayer.ageCategory || '';
        setAgeCategory(nextAge);
        setCompareAges(nextAge ? [nextAge] : []);
      }
    }

    if (tab !== 'learn' && tab !== 'benchmark') {

      setCompareMode(false);

      setCompareAges([ageCategory]);

      setSurveyOpen(false);

    }

    if (tab !== 'learn') {

      setSurveyOpen(false);

    }

  };



  const loginReturnPath = `/contests/${sportSlug}?tab=${
    contestTabToQuery(activeTab) ?? 'learn'
  }&age=${encodeURIComponent(ageCategory)}`;



  const showSubmissionTicks = Boolean(contestPlayerId) && activeTab === 'learn';

  const survey = useMemo(() => getContestSurvey(sportName), [sportName]);

  /** Keep frames viewport-bound so Frame 1 scrolls inside the card (not the whole page). */
  const resolvedFrameHeight = frameHeight ?? {
    xs: 'min(62vh, 520px)',
    md: 'min(68vh, 580px)',
    lg: 'calc(100dvh - 360px)',
  };



  if (!hasPreview && !catalogLoading) {

    return (

      <Alert severity="info" sx={{ borderRadius: 2 }}>

        Contest catalog preview is not available for {sportName} yet.

      </Alert>

    );

  }

  if (catalogLoading && !sportCatalog) {

    return (

      <Alert severity="info" sx={{ borderRadius: 2 }}>

        Loading contest catalog…

      </Alert>

    );

  }



  return (

    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%' }}>

      {showCatalogSourceHint && catalogSource === 'db' && (
        <Alert severity="success" sx={{ borderRadius: 2, py: 0.5 }}>
          Frame 1: live database catalog ({skillCount} skill{skillCount === 1 ? '' : 's'}).
        </Alert>
      )}
      {showCatalogSourceHint && catalogSource === 'seed' && (
        <Alert severity="warning" sx={{ borderRadius: 2, py: 0.5 }}>
          Frame 1: JSON seed preview (no skills in database for this sport yet).
        </Alert>
      )}

      {missingDob && (activeTab === 'self_evaluation' || activeTab === 'contest') && (
        <Alert
          severity="warning"
          sx={{ borderRadius: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              sx={{ fontWeight: 800, textTransform: 'none' }}
              onClick={() => navigate('/player/profile')}
            >
              Add date of birth
            </Button>
          }
        >
          {contestPlayerName
            ? `${contestPlayerName} has no date of birth on this profile.`
            : 'This account needs a player profile with a date of birth.'}{' '}
          Age category is calculated from date of birth, so Self Evaluation and Contest stay locked until you add it.
          A parent should show Senior (or the matching adult band), not a child category such as U13.
        </Alert>
      )}

      <ContestJourneyHeader

        sportName={sportName}

        sportIcon={getSportIcon(sportName, 24)}

        activeTab={activeTab}

        onTabChange={handleTabChange}

        ageCategory={ageCategory}

        categories={
          activeTab === 'contest' || activeTab === 'self_evaluation'
            ? actingPlayer?.ageCategory
              ? [actingPlayer.ageCategory]
              : []
            : CONTEST_AGE_CATEGORIES
        }

        ageLocked={activeTab === 'contest' || activeTab === 'self_evaluation'}

        onAgeChange={handleAgeChange}

        compareMode={compareMode}

        compareAges={compareAges}

        profileSlot={null}

        ageLeading={
          activeTab === 'self_evaluation' || activeTab === 'contest' ? (
            <Chip
              size="small"
              label={getContestGenderLabel(ageCategory, genderKey)}
              sx={{
                fontWeight: 800,
                bgcolor: brandColors.white,
                color: brandColors.navy,
                border: `1px solid ${brandColors.navy}44`,
              }}
            />
          ) : null
        }

        ageTrailing={

          activeTab === 'learn' ? (

            <ContestLearnAgeActions

              compareMode={compareMode}

              surveyOpen={surveyOpen}

              onCompareModeChange={handleCompareModeChange}

              onSurveyOpenChange={setSurveyOpen}

              showCompareVideos

              onCompareVideosClick={() => setVideoCompareOpen(true)}

            />

          ) : activeTab === 'benchmark' ? (

            <ContestLearnAgeActions

              compareMode={compareMode}

              surveyOpen={false}

              onCompareModeChange={handleCompareModeChange}

              onSurveyOpenChange={setSurveyOpen}

              showSurvey={false}

            />

          ) : activeTab === 'contest' && contestPlayerId ? (

            <Typography variant="body2" sx={{ color: brandColors.slate, flexShrink: 0 }}>

              Submitted: <strong>{submittedIds.length}</strong> / {allContests.length}

            </Typography>

          ) : null

        }

        belowAge={

          (activeTab === 'learn' || activeTab === 'benchmark') && compareMode ? (

            <Box sx={{ px: { xs: 1.25, md: 1.75 }, pb: 1.25, bgcolor: brandColors.ice, borderTop: '1px solid #e2e8f0' }}>

              <ContestBenchmarkComparePanel contest={selectedContest} compareAges={compareAges} embedded />

            </Box>

          ) : activeTab === 'learn' && surveyOpen ? (

            <Box sx={{ px: { xs: 1.25, md: 1.75 }, pb: 1.25, bgcolor: brandColors.ice, borderTop: '1px solid #e2e8f0' }}>

              <ContestSurveyPanel survey={survey} onClose={() => setSurveyOpen(false)} />

            </Box>

          ) : null

        }

      />


      {activeTab === 'benchmark' && (
        <ContestBenchmarkTabPanel

          categoryGroups={categoryGroups}

          ageCategory={ageCategory}

          sportName={sportName}

          selectedContestId={selectedContestId}

          onSelectContest={setSelectedContestId}

        />

      )}

      {activeTab === 'sports_iq' && <ContestSportsIqTabPanel sportName={sportName} />}



      {activeTab === 'learn' && (

        <Box

          sx={{

            display: 'grid',

            gridTemplateColumns: COLUMN_GRID,

            gap: { xs: 1.5, lg: 1.75 },

            alignItems: 'stretch',

            width: '100%',

            position: { lg: 'sticky' },

            top: { lg: 12 },

            alignSelf: 'start',

          }}

        >

          <ContestColumn>

            <ContestFrameShell

              tone="primary"

              integratedTitle={foundationLabel}

              ageBadge={ageCategory}

              titleAccent={brandColors.blue}

              frameHeight={resolvedFrameHeight}

            >

              <ContestScorecardPanel

                categoryGroups={categoryGroups}

                selectedContestId={selectedContest?.id ?? null}

                ageCategory={ageCategory}

                compactList

                submittedContestIds={showSubmissionTicks ? submittedIds : []}

                onSelectContest={(contestId) => handleSelectContest(contestId)}

              />

            </ContestFrameShell>

          </ContestColumn>



          <ContestColumn>

            <Box ref={detailsColumnRef} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%', flex: 1, scrollMarginTop: 16 }}>

            <ContestFrameShell

              tone="secondary"

              integratedTitle="Skill and Performance Guidelines"

              ageBadge={ageCategory}

              titleAccent={brandColors.navy}

              titleIcon={<AssignmentIcon sx={{ fontSize: 18, color: brandColors.navy }} />}

              frameHeight={resolvedFrameHeight}

            >

              <ContestDetailAccordion

                contest={selectedContest}

                ageCategory={ageCategory}

                activeSectionKey={activeSectionKey}

                onSectionChange={setActiveSectionKey}

                onSubmit={() => handleTabChange('contest')}

                onSelfEvaluate={() => handleTabChange('self_evaluation')}

                browseOnly

              />

            </ContestFrameShell>

            </Box>

          </ContestColumn>



          <ContestColumn>

            <ContestFrameShell

              tone="secondary"

              integratedTitle="Learning Videos"

              ageBadge={ageCategory}

              titleAccent={brandColors.blue}

              titleIcon={<VideoIcon sx={{ fontSize: 18, color: brandColors.blue }} />}

              frameHeight={resolvedFrameHeight}

            >

              <ContestVideoPanel

                contest={selectedContest}

                activeSectionKey={activeSectionKey}

                activeVideoId={activeVideoId}

                onSelectVideo={setActiveVideoId}

                ageCategory={ageCategory}

              />

            </ContestFrameShell>

          </ContestColumn>

        </Box>

      )}



      {activeTab === 'self_evaluation' && (

        <ContestSelfEvalTabPanel

          sportName={sportName}

          ageCategory={ageCategory}

          genderKey={genderKey}

          loginReturnPath={loginReturnPath}

          onLoginRequired={(path) => navigate(`/login?returnUrl=${encodeURIComponent(path)}`)}

          playerId={contestPlayerId}

          playerName={contestPlayerName}

          actingKind={actingPlayer?.kind}

          viewOnly={contestViewOnly}

          missingDob={missingDob}

          eligibleAgeCategory={profileAgeCategory || undefined}

          profileSlot={
            signedIn ? (
              actingPlayer ? (
                <ContestActingPlayerSwitch
                  selected={actingPlayer}
                  options={actingOptions}
                  canSwitch={canSwitchActingPlayer}
                  onChange={setActingPlayerId}
                  accent={brandColors.violet}
                />
              ) : contestPlayerName ? (
                <Chip
                  size="small"
                  label={contestPlayerName}
                  sx={{
                    fontWeight: 800,
                    bgcolor: `${brandColors.violet}14`,
                    color: brandColors.violet,
                    border: `1px solid ${brandColors.violet}55`,
                  }}
                />
              ) : null
            ) : null
          }

        />

      )}



      {activeTab === 'contest' && (

        <ContestSubmissionTabPanel

          sportName={sportName}

          ageCategory={ageCategory}

          genderKey={genderKey}

          playerId={contestPlayerId}

          playerName={contestPlayerName}

          userId={userId || user?.id}

          isAuthenticated={signedIn}

          onLoginRequired={(path) => navigate(path)}

          onGoToSelfEval={() => handleTabChange('self_evaluation')}

          viewOnly={contestViewOnly}

          missingDob={missingDob}

          eligibleAgeCategory={profileAgeCategory || undefined}

          actingKind={actingPlayer?.kind}

          profileSlot={
            signedIn ? (
              actingPlayer ? (
                <ContestActingPlayerSwitch
                  selected={actingPlayer}
                  options={actingOptions}
                  canSwitch={canSwitchActingPlayer}
                  onChange={setActingPlayerId}
                  accent={brandColors.orange}
                />
              ) : contestPlayerName ? (
                <Chip
                  size="small"
                  label={contestPlayerName}
                  sx={{
                    fontWeight: 800,
                    bgcolor: `${brandColors.orange}14`,
                    color: brandColors.orange,
                    border: `1px solid ${brandColors.orange}55`,
                  }}
                />
              ) : null
            ) : null
          }

        />

      )}



      {activeTab === 'leaderboard' && (

        <ContestLeaderboardTabPanel sportName={sportName} ageCategory={ageCategory} />

      )}

      <ContestVideoCompareDialog
        open={videoCompareOpen}
        onClose={() => setVideoCompareOpen(false)}
        contest={selectedContest}
        primaryAge={ageCategory}
      />

    </Box>

  );

};



export default ContestCatalogExperience;


